import { Fragment } from 'preact';
import type { JSX } from 'preact';
import { useEffect, useRef } from 'preact/hooks';
import type { UiMessage } from '../hooks/useChat';
import { reportClickthrough } from '../lib/clickthrough';
import { MessageBubble } from './MessageBubble';
import { ProductCard } from './ProductCard';
import { TypingIndicator } from './TypingIndicator';

interface MessageListProps {
  messages: UiMessage[];
  onSend: (text: string) => void;
  onRetry: () => void;
  apiBaseUrl: string;
}

const SUGGESTIONS = ['أبحث عن رواية مترجمة', 'اقترح لي كتاب فلسفة'];

export function MessageList({
  messages,
  onSend,
  onRetry,
  apiBaseUrl,
}: MessageListProps): JSX.Element {
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages]);

  if (messages.length === 0) {
    return (
      <div class="sa-messages">
        <div class="sa-empty">
          <h3>مرحباً بك في مكتبة صوفيا</h3>
          <p>كيف أساعدك في العثور على كتابك؟</p>
          <div class="sa-suggestions">
            {SUGGESTIONS.map((text) => (
              <button type="button" class="sa-chip" key={text} onClick={() => onSend(text)}>
                {text}
              </button>
            ))}
          </div>
        </div>
        <div ref={endRef} />
      </div>
    );
  }

  return (
    <div class="sa-messages" role="log" aria-live="polite">
      {messages.map((message, index) => {
        const isPendingAssistant =
          message.role === 'assistant' && message.status === 'streaming' && message.text === '';
        const canRetry = message.status === 'error' && index === messages.length - 1;

        return (
          <Fragment key={message.id}>
            {isPendingAssistant ? (
              <div class="sa-row assistant">
                <TypingIndicator />
              </div>
            ) : (
              <MessageBubble message={message} />
            )}

            {canRetry && (
              <button type="button" class="sa-retry" onClick={onRetry}>
                إعادة المحاولة
              </button>
            )}

            {message.products.length > 0 && (
              <div class="sa-cards">
                {message.products.map((card) => (
                  <ProductCard
                    key={card.product.id}
                    card={card}
                    onNotify={(title) => onSend(`أرغب أن تعلمني عند توفّر «${title}».`)}
                    onView={() => reportClickthrough(apiBaseUrl, card.product)}
                  />
                ))}
              </div>
            )}
          </Fragment>
        );
      })}
      <div ref={endRef} />
    </div>
  );
}
